/**
 * `geometry/scale-entities` × `Table` — the ONE truth for "this SCALE declines
 * this entity", shared by the geometry DOCUMENT reducer (which skips the
 * declined ids) and the SHELL reducer (which reports the refusal to the user).
 *
 * Same two-site shape as REVCLOUD's `resolveRevcloudObjectSource`: the
 * validity predicate lives here in `command-engine` so the document reducer's
 * guard and the shell reducer's audit message can never drift apart.
 *
 * ## Why a table declines a resize — KNOWN INCOMPLETE SUBSYSTEM
 *
 * GitHub Codex review PR #785 round 2, P2 ("Scale the table contents along
 * with its insertion point") — before this module, `geometry/scale-entities`
 * transformed ONLY a table's `insertionPoint` (plus `horizontalDirection`,
 * which {@link import("./geometryTransforms").transformEntityCoords}'s `Table`
 * arm deliberately leaves bit-identical under a positive uniform scale). The
 * table therefore slid to a scaled POSITION at its ORIGINAL SIZE while every
 * neighbour grew — silent geometric corruption.
 *
 * A faithful resize is NOT reachable from the entity's own numbers:
 *
 *  1. `rowHeights` (DXF 141) / `columnWidths` (142) on `tableEntitySchema`
 *     (`drawing-model/entityPolylineTableOle.ts`) are READ-ONLY PROJECTIONS of
 *     `contentGroups`, the single write source — the DXF writer
 *     (`dxf-io/writer.ts`) emits `contentGroups` verbatim and never re-emits
 *     141/142 from those arrays.
 *  2. The table's VISIBLE geometry is not drawn from those numbers at all. It
 *     is the PRE-EVALUATED `*T<N>` anonymous graphics block — one MTEXT per
 *     cell plus the LINE grid — projected through
 *     `drawing-model/tableBlockReference.ts`'s `tableAsBlockReference`, which
 *     hard-codes `scaleX/scaleY/scaleZ = 1` because AcDbTable carries no scale
 *     factors at all.
 *
 * So scaling 141/142 inside `contentGroups` would change the DATA and leave
 * the rendered PICTURE identical — data and graphics desynced, which is worse
 * than the pre-fix half-transform. A faithful resize needs the `*T<N>`
 * graphics scaled or regenerated in step, and this codebase has no table
 * layout engine to regenerate them with.
 *
 * COMPLETING THIS requires, in its own PR: a table layout engine that rebuilds
 * (or affinely rescales) the `*T<N>` block content — cell MTEXT heights and
 * insertion points, the LINE grid — from `contentGroups`, wired so that a
 * write to 141/142 and the block rebuild happen in ONE `EntityMutation`; only
 * then can this predicate be deleted and `transformEntityCoords`'s `Table` arm
 * grow the row/column arithmetic. Until then the refusal is VISIBLE (an RU+EN
 * warning-tone audit row) rather than silent, per CLAUDE.md ПРИОРИТЕТ #1's one
 * allowed exception: a documented, known-incomplete subsystem.
 *
 * ## Why `|factor| !== 1` and not `factor !== 1`
 *
 * `scalePoint` applies ONE scalar to both axes, so the SIZE change is
 * `|factor|`. At `factor === 1` the command is a no-op — nothing to refuse and
 * nothing to report. At `factor === -1` it is a POINT REFLECTION: sizes are
 * unchanged (`|-1| === 1`), the insertion point reflects, and the linear part
 * `diag(-1, -1)` turns `horizontalDirection` by exactly 180° with its
 * magnitude intact — which `tableRotationDegrees` then feeds to the renderer,
 * the bounds view and the DXF writer alike. That transform is already EXACT
 * for a table, so refusing it would introduce the very desync this module
 * exists to prevent. Only a genuine RESIZE (`|factor| !== 1`) is declined.
 */

import type { DrawingEntity, DrawingSpace } from "@dxf-ai-editor/drawing-model";

import {
  summarizeSelectionRefusal,
  type SelectionRefusalSummary,
} from "./selectionRefusalWalk";

/**
 * `true` when `geometry/scale-entities` must DECLINE `entity` at `factor`
 * rather than half-transform it. See the module header for the full reason.
 *
 * Total and pure: every non-`Table` entity, and every `Table` under a
 * size-preserving `|factor| === 1`, scales normally.
 */
export function isTableScaleRefused(
  entity: DrawingEntity,
  factor: number,
): boolean {
  return entity.type === "Table" && Math.abs(factor) !== 1;
}

/**
 * What a `geometry/scale-entities` dispatch will actually do to its target set.
 *
 * The SHAPE is the one every refusal shares
 * ({@link import("./selectionRefusalWalk").SelectionRefusalSummary} — declined
 * ids plus a transformed count); this alias keeps SCALE's own call sites
 * reading in SCALE's vocabulary and keeps the public name stable.
 */
export type TableScaleRefusalSummary = SelectionRefusalSummary;

/**
 * Resolve `entityIds` against `spaces` and split them into "declined" and
 * "will scale", using {@link isTableScaleRefused} as the single predicate.
 *
 * The walk itself — id resolution, de-duplication, "an id that matches nothing
 * counts as neither", document order — is
 * {@link import("./selectionRefusalWalk").summarizeSelectionRefusal}, the ONE
 * such walk this package has, shared with the OLE2FRAME transform refusal
 * (`oleFrameTransformRefusal.ts`). Only the predicate above and the message
 * ids the shell reducer picks are SCALE's own.
 */
export function summarizeTableScaleRefusal(
  spaces: readonly DrawingSpace[],
  entityIds: readonly string[],
  factor: number,
): TableScaleRefusalSummary {
  return summarizeSelectionRefusal(spaces, entityIds, (entity) =>
    isTableScaleRefused(entity, factor),
  );
}
